import { useEffect, useState } from 'react'
import { ClockCounterClockwiseIcon } from '@phosphor-icons/react'
import { format, parseISO } from 'date-fns'

import { emotionTheme } from '@styles/theme'
import { formattedDatePtBrDateOnly } from '@utils/dates/formattedDatePtBr'
import { currencyMask } from '@utils/masks/currencyMask'
import { Table } from '@components/shared/Table'
import { toast } from '@utils/functions/notificationToast'
import { useUser } from '@hooks/useUser'
import * as apiPeople from '@services/api/people'

import type { AxiosError } from 'axios'
import type { ColumnDefProps } from '@components/shared/Table/types'
import type { GeneralAdjustHistoryResponseProps } from '@services/api/people'

import * as S from '../styles'

const ITEMS_PER_PAGE = 5
const EMPTY = '–'
const totalsRowBg = emotionTheme.colors.gray?.[100] ?? '#f5f5f5'

const formatDateTime = (value: string) => (value ? format(parseISO(value), 'dd/MM/yyyy HH:mm') : EMPTY)

// Ajustes anteriores à coluna de data vêm com DateTime.MinValue ("0001-01-01T00:00:00")
const formatAdjustmentDate = (value: string | null) =>
  !value || value.startsWith('0001-') ? EMPTY : formattedDatePtBrDateOnly(value) || EMPTY

const formatCurrency = (value: number) => currencyMask(value) || currencyMask('0')

const parseHistoryGrid = (data: GeneralAdjustHistoryResponseProps | null): ColumnDefProps[][] => {
  // CA03: sem ajustes, exibe uma linha com "–"
  const rows = data?.items?.length ? data.items : [null]

  return rows.map((item) => [
    {
      id: 'adjustmentDate',
      columnName: 'Data',
      align: 'center',
      minWidth: '6.5rem',
      maxWidth: '7rem',
      value: item ? formatAdjustmentDate(item.adjustmentDate) : EMPTY
    },
    {
      id: 'totalAmount',
      columnName: 'Valor',
      align: 'center',
      minWidth: '7.5rem',
      maxWidth: '8.5rem',
      value: item ? formatCurrency(item.totalAmount) : EMPTY
    },
    {
      id: 'observations',
      columnName: 'Observações',
      align: 'left',
      minWidth: '14rem',
      maxWidth: 'none',
      withoutTooltip: true,
      // RT11: observação completa, sem truncamento
      value: item ? <S.FullText>{item.observations}</S.FullText> : EMPTY
    },
    { id: 'userName', columnName: 'Alterado por', align: 'center', minWidth: '7rem', maxWidth: '9rem', value: item?.userName || EMPTY },
    {
      id: 'createdAt',
      columnName: 'Data e Hora',
      align: 'center',
      minWidth: '8.5rem',
      maxWidth: '9rem',
      value: item ? formatDateTime(item.createdAt) : EMPTY
    }
  ])
}

const parseHistoryTotals = (data: GeneralAdjustHistoryResponseProps | null): ColumnDefProps[][] =>
  data?.items?.length
    ? [
        [
          {
            id: 'totalRecords',
            columnName: '',
            colSpan: 2,
            columnColor: totalsRowBg,
            withoutTooltip: true,
            value: <b>Total de registros: {data.totalItems}</b>
          },
          {
            id: 'totalAmount',
            columnName: '',
            colSpan: 3,
            columnColor: totalsRowBg,
            withoutTooltip: true,
            value: <b>Total Ajustes: {formatCurrency(data.totalAmount)}</b>
          }
        ]
      ]
    : []

interface AdjustHistoryProps {
  /** Incrementar força recarga (ex.: após confirmar novo ajuste) */
  refreshKey: number
}

export const AdjustHistory = ({ refreshKey }: AdjustHistoryProps) => {
  const { filiData, userData } = useUser()

  const [currentPage, setCurrentPage] = useState(1)
  const [data, setData] = useState<GeneralAdjustHistoryResponseProps | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  // Sem filtros na tela: o histórico segue a competência selecionada no header (RN08)
  const startPeriod = filiData?.startPeriod ?? ''
  const period = userData?.competencePeriod?.find((item) => item.monthNumber === Number(startPeriod.slice(5, 7)))?.periodCode

  useEffect(() => {
    setCurrentPage(1)
  }, [filiData?.filiCode, startPeriod])

  useEffect(() => {
    if (!filiData?.filiCode) return

    setIsLoading(true)

    apiPeople
      .getGeneralAdjustHistory(filiData.filiCode, {
        period,
        referenceDate: startPeriod ? startPeriod.slice(0, 10) : undefined,
        currentPage,
        itemsPerPage: ITEMS_PER_PAGE
      })
      .then(setData)
      .catch((error) => {
        setData(null)
        toast.error(
          (error as AxiosError<{ errorMessage: string[] }>)?.response?.data?.errorMessage?.[0] ||
            'Não foi possível carregar o histórico de ajustes',
          (error as AxiosError)?.status
        )
      })
      .finally(() => setIsLoading(false))
  }, [filiData?.filiCode, startPeriod, period, currentPage, refreshKey])

  return (
    <S.History>
      <S.HistoryTitle>
        <ClockCounterClockwiseIcon size={18} />
        Histórico de Ajustes
      </S.HistoryTitle>

      <Table
        data={parseHistoryGrid(data)}
        totalData={parseHistoryTotals(data)}
        isLoading={isLoading}
        footer={
          data?.items?.length
            ? {
                pagination: {
                  currentPage: data.currentPage ?? currentPage,
                  itemsPerPage: data.itemsPerPage ?? ITEMS_PER_PAGE,
                  totalItems: data.totalItems ?? 0,
                  totalPages: data.totalPages ?? 1,
                  onPageChange: setCurrentPage
                }
              }
            : null
        }
      />
    </S.History>
  )
}
